import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Text,
  EntitySelectorStepHeader,
  toastManager,
  type BulkActionConfirm,
  type BulkActionDirect,
  type BulkActionPopover,
  type SelectAllGroup,
} from "@salestrak/ui";
import { dataToField, fieldDto } from "@salestrak/custom-fields";
import type { IssueRow, StageOption } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import {
  StageSelector,
  AssigneeSelector,
  CustomFieldSelector,
  IssueSourceSelector,
  TeamSelector,
  WorkflowSelector,
} from "#app/components/entity-selectors";
import { CustomFieldRenderer } from "#app/components/custom-fields/custom-field-renderers";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { MovedToTrashTitle } from "#app/components/settings/moved-to-trash-title";
import type { IssueSelector } from "#app/hooks/issue-list/use-issue-list-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { UNDO_TOAST_SHORTCUT, UNDO_TOAST_TIMEOUT_MS } from "#app/lib/undo-shortcut";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  BulkOperation,
  BulkMode,
  type AnalyticsScope,
  type AnalyticsView,
} from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

/**
 * The shared bulk-action popover bodies for issue selections — extracted from
 * `issues-table.tsx` so anything listing issue rows (the list view, a widget's
 * drilldown dialog) mounts the SAME stage/assignee editors instead of forking
 * them. Callers pass the analytics `view`/`scope` so each reports where the
 * bulk operation happened; the mutation callbacks arrive as
 * {@link BulkIssueMutations} (from `useIssueListMutations().bulk`).
 */

/** Dedicated bulk mutation callbacks — single call per operation. */
export type BulkIssueMutations = {
  changeStage: (issueIds: string[], stageId: string) => Promise<void>;
  changeStageByWhere: (
    where: Record<string, unknown>,
    stageId: string,
  ) => Promise<void>;
  addAssignees: (issueIds: string[], entityIds: string[]) => Promise<void>;
  addAssigneesByWhere: (
    where: Record<string, unknown>,
    entityIds: string[],
  ) => Promise<void>;
  removeAssignees: (issueIds: string[], entityIds: string[]) => Promise<void>;
  removeAssigneesByWhere: (
    where: Record<string, unknown>,
    entityIds: string[],
  ) => Promise<void>;
  setPrimaryAssignee: (issueIds: string[], entityId: string) => Promise<void>;
  setPrimaryAssigneeByWhere: (
    where: Record<string, unknown>,
    entityId: string,
  ) => Promise<void>;
  // One request per batch each, and each rejects the batch when the server
  // refuses any issue in it.
  changeTeams: (selector: IssueSelector, add: string[], remove: string[]) => Promise<void>;
  moveToWorkflow: (
    selector: IssueSelector,
    workflowId: string,
    stageId: string,
  ) => Promise<void>;
  setSource: (selector: IssueSelector, issueSourceId: string) => Promise<void>;
  setCustomFieldValue: (
    selector: IssueSelector,
    customFieldId: string,
    value: unknown,
  ) => Promise<void>;
  archiveIssues: (selector: IssueSelector) => Promise<void>;
  unarchiveIssues: (selector: IssueSelector) => Promise<void>;
  /** Resolves to the ids that reached the trash. */
  deleteIssues: (issueIds: string[]) => Promise<string[]>;
  restoreIssues: (issueIds: string[]) => Promise<void>;
};

export type BulkCommit<TData> = (
  fn: (rows: TData[]) => Promise<void> | void,
  successMessage?: string,
) => void;

/**
 * When select-all is active for one or more groups, merge their where clauses
 * into a single where object. Returns undefined when no groups are select-all.
 */
export function buildSelectAllWhere(
  selectAllGroups: SelectAllGroup[],
  getGroupWhere: (groupKey: string) => Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (selectAllGroups.length === 0) return undefined;
  if (selectAllGroups.length === 1)
    return getGroupWhere(at(selectAllGroups, 0, "select-all group").groupKey);
  return { OR: selectAllGroups.map((g) => getGroupWhere(g.groupKey)) };
}

function pluralProjects(n: number) {
  return `${n.toLocaleString()} Project${n !== 1 ? "s" : ""}`;
}

/**
 * Above this many projects a bulk Stage or Assignee change asks first. A
 * select-all selection always asks: it reaches rows that are not loaded, so
 * its size is a server count the viewer may never have looked at.
 */
export const BULK_CONFIRM_THRESHOLD = 50;

export function needsBulkConfirm(
  count: number,
  selectAllWhere: Record<string, unknown> | undefined,
): boolean {
  return selectAllWhere !== undefined || count > BULK_CONFIRM_THRESHOLD;
}

/**
 * The confirm step for a large bulk change, drawn INSIDE the action's popover
 * in place of its picker: a dialog opened from the popover would count as an
 * outside press and close the popover under it. Cancel returns to the picker.
 */
function BulkConfirmPanel({
  title,
  confirmLabel,
  onConfirm,
  onCancel,
}: Omittable<{
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}>) {
  return (
    <Box direction="column" gap="sm" padding="sm">
      <Text size="sm" weight="medium">
        {title}
      </Text>
      <Text size="xs" variant="muted">
        This changes every selected project at once and cannot be undone.
      </Text>
      <Box direction="row" gap="xs" justify="end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={onConfirm} autoFocus>
          {confirmLabel}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * The bulk Delete action every issue view mounts: an icon-only trash on the
 * bar (its label rides the tooltip), confirmed before it fires, and moving
 * the selection to the trash. Hidden while a selection reaches rows the
 * viewer cannot delete, and while select-all is active — the API deletes by
 * id only, so unloaded rows could not be reached and the loaded subset would
 * be a silent partial delete. The toast offers Undo (and ⌘Z), like the
 * single-item delete: it restores the same ids one restore call each.
 */
export function buildBulkDeleteAction({
  view,
  scope,
  bulkMutations,
  track,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  track: ReturnType<typeof useAnalytics>["track"];
}>): BulkActionConfirm<IssueRow> {
  return {
    id: "delete",
    type: "confirm",
    label: "Delete",
    dialogLabel: "Delete...",
    icon: "trash",
    destructive: true,
    // Hidden under select-all (the API deletes by id only); a selection the
    // viewer can't fully delete keeps the button, disabled with the reason.
    hidden: (rows, selectAllGroups) =>
      selectAllGroups.length > 0 || !rows.some((r) => r.viewerCanDelete),
    disabledReason: (rows) =>
      rows.some((r) => !r.viewerCanDelete)
        ? "Includes a project you can't delete"
        : undefined,
    confirmTitle: (rows) => `Delete ${pluralProjects(rows.length)}?`,
    confirmDescription: (rows) =>
      `${pluralProjects(rows.length)} will move to the trash, where ${rows.length === 1 ? "it" : "they"} can be restored.`,
    confirmLabel: "Delete",
    successMessage: (rows) => (
      <MovedToTrashTitle subject={pluralProjects(rows.length)} />
    ),
    onAction: async (rows) => {
      const issueIds = await bulkMutations.deleteIssues(rows.map((r) => r.issueId));
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.BulkDelete,
        data: { view, scope, mode: BulkMode.Ids, count: rows.length },
      });
      return {
        onUndo: () => {
          void bulkMutations.restoreIssues(issueIds);
        },
        shortcut: UNDO_TOAST_SHORTCUT,
        timeout: UNDO_TOAST_TIMEOUT_MS,
      };
    },
  };
}

/**
 * Bulk Archive: the `viewerCanArchive` mirror of bulk Delete, an icon-only
 * glyph beside the trash — in a CRM a batch of dead leads is archived far
 * more often than deleted, so the commoner verb sits on the strip too.
 * One `bulkArchiveIssues` request for the batch, which fails when the server
 * refuses any row; like Delete it steps aside under select-all (unloaded rows are out of
 * reach by id), disables with the reason while any selected row lacks the
 * viewer's archive grant, and skips rows already archived. A selection that
 * is ALL archived rows hands the slot to {@link buildBulkUnarchiveAction}.
 */
export function buildBulkArchiveAction({
  view,
  scope,
  bulkMutations,
  track,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  track: ReturnType<typeof useAnalytics>["track"];
}>): BulkActionDirect<IssueRow> {
  return {
    id: "archive",
    type: "direct",
    label: "Archive",
    icon: "archive",
    iconOnly: true,
    // Hidden under select-all and when nothing selected is still open; a
    // row the viewer can't archive disables it with the reason; rows already
    // archived are skipped and the toast counts the ones reached.
    hidden: (rows, selectAllGroups) =>
      selectAllGroups.length > 0 || !rows.some((r) => !r.archived),
    disabledReason: (rows) =>
      rows.some((r) => !r.viewerCanArchive)
        ? "Includes a project you can't archive"
        : undefined,
    successMessage: (rows) =>
      `${pluralProjects(rows.filter((r) => !r.archived).length)} archived`,
    onAction: async (rows) => {
      const open = rows.filter((r) => !r.archived);
      await bulkMutations.archiveIssues({ issueIds: open.map((r) => r.issueId) });
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.BulkArchive,
        data: { view, scope, mode: BulkMode.Ids, count: open.length },
      });
    },
  };
}

/**
 * "Assign to me": the first dialog-only (`overflow`) bulk action — it earns
 * no strip button, but mounting it is what gives every project view the ⋯
 * More trigger and its command dialog. Adds the viewer to the selection's
 * assignees (a no-op on rows that already carry them). Hidden while
 * select-all is active: the by-id mutation cannot reach unloaded rows, the
 * same rule bulk Delete follows.
 */
export function buildAssignToMeAction({
  view,
  scope,
  bulkMutations,
  currentUserId,
  track,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  /** The viewer's user id, which IS the assignee entity id. */
  currentUserId: string;
  track: ReturnType<typeof useAnalytics>["track"];
}>): BulkActionDirect<IssueRow> {
  return {
    id: "assign-me",
    type: "direct",
    label: "Assign to Me",
    icon: "user-check",
    overflow: true,
    hidden: (_rows, selectAllGroups) => selectAllGroups.length > 0,
    successMessage: (_rows, count) =>
      `${pluralProjects(count)} assigned to you`,
    onAction: async (rows) => {
      await bulkMutations.addAssignees(
        rows.map((r) => r.issueId),
        [currentUserId],
      );
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.BulkUpdateAssignees,
        data: {
          view,
          scope,
          operation: BulkOperation.Add,
          mode: BulkMode.Ids,
          count: rows.length,
        },
      });
    },
  };
}

export function BulkStageContent({
  view,
  scope,
  workflowId,
  commit,
  bulkMutations,
  refetchGroups,
  availableStages,
  selectedCount,
  selectedRows,
  selectAllWhere,
}: Omittable<{
  /** Which view the bulk operation happened on, for analytics. */
  view: AnalyticsView;
  /** The scope kind that view was showing, for analytics. */
  scope: AnalyticsScope;
  workflowId: string;
  commit: BulkCommit<IssueRow>;
  bulkMutations: BulkIssueMutations;
  refetchGroups: () => Promise<unknown>;
  availableStages: StageOption[];
  selectedCount: number;
  /** The loaded selection, so the toast counts only the projects that moved.
   *  A select-all move cannot see its unloaded rows, so it reports the count. */
  selectedRows?: IssueRow[];
  selectAllWhere?: Record<string, unknown>;
}>) {
  const { track } = useAnalytics();
  const [pendingStageId, setPendingStageId] = useState<string | null>(null);
  const stageNameOf = (stageId: string) =>
    availableStages.find((s) => s.stageId === stageId)?.name ?? "stage";

  const apply = (stageId: string) => {
    const stageName = stageNameOf(stageId);
    // The mutation returns every selected row, moved or not, so the count
    // that moved comes from the loaded rows' own stage before the move.
    // A select-all whose rows are all loaded (a small group) counts the same way.
    const countable =
      selectedRows !== undefined &&
      (!selectAllWhere || selectedRows.length >= selectedCount);
    const moved = countable
      ? selectedRows.filter((r) => r.stageId !== stageId).length
      : selectedCount;
    commit(
      async (rows) => {
        if (selectAllWhere) {
          await bulkMutations.changeStageByWhere(selectAllWhere, stageId);
        } else {
          await bulkMutations.changeStage(
            rows.map((r) => r.issueId),
            stageId,
          );
        }
        await refetchGroups();
        track({
          context: AnalyticsContext.Issue,
          event: IssueEvent.BulkChangeStage,
          data: {
            view,
            scope,
            mode: selectAllWhere ? BulkMode.Where : BulkMode.Ids,
            count: selectedCount,
          },
        });
      },
      moved === 0
        ? `Already in ${stageName}`
        : `${pluralProjects(moved)} moved to ${stageName}`,
    );
  };

  if (pendingStageId !== null) {
    return (
      <BulkConfirmPanel
        title={`Move ${pluralProjects(selectedCount)} to ${stageNameOf(pendingStageId)}?`}
        confirmLabel="Move"
        onConfirm={() => apply(pendingStageId)}
        onCancel={() => setPendingStageId(null)}
      />
    );
  }

  return (
    <StageSelector
      variant="inline"
      workflowId={workflowId}
      onValueChange={(stageId) => {
        if (needsBulkConfirm(selectedCount, selectAllWhere)) {
          setPendingStageId(stageId);
          return;
        }
        apply(stageId);
      }}
    />
  );
}

export function BulkAssigneeContent({
  view,
  scope,
  bulkMutations,
  selectedRows,
  selectedCount,
  selectAllWhere,
}: Omittable<{
  /** Which view the bulk operation happened on, for analytics. */
  view: AnalyticsView;
  /** The scope kind that view was showing, for analytics. */
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  selectedRows: IssueRow[];
  /** The selection's full size under select-all (unloaded rows included);
   *  the loaded rows' count otherwise. */
  selectedCount?: number;
  selectAllWhere?: Record<string, unknown>;
}>) {
  const { track } = useAnalytics();
  // Not every changed row is on screen, so the toast says how many it reached.
  const reached = pluralProjects(selectedCount ?? selectedRows.length);
  // Union of all assignee IDs across selected issues
  const initialIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of selectedRows) {
      for (const a of row.assignees) ids.add(a.entityId);
    }
    return Array.from(ids);
  }, [selectedRows]);

  const [value, setValue] = useState(initialIds);
  const prevRef = useRef(initialIds);

  const issueIds = useMemo(
    () => selectedRows.map((r) => r.issueId),
    [selectedRows],
  );

  const count = selectedCount ?? selectedRows.length;
  const confirmFirst = needsBulkConfirm(count, selectAllWhere);
  // A large change waits here for the confirm panel; small ones apply at once.
  const [pending, setPending] = useState<
    { kind: "value"; ids: string[] } | { kind: "primary"; entityId: string } | null
  >(null);

  const applyValueChange = useCallback(
    (nextIds: string[]) => {
      const prevSet = new Set(prevRef.current);
      const nextSet = new Set(nextIds);

      const added = nextIds.filter((id) => !prevSet.has(id));
      const removed = prevRef.current.filter((id) => !nextSet.has(id));

      prevRef.current = nextIds;
      setValue(nextIds);

      const mode = selectAllWhere ? BulkMode.Where : BulkMode.Ids;

      if (added.length > 0) {
        const p = selectAllWhere
          ? bulkMutations.addAssigneesByWhere(selectAllWhere, added)
          : bulkMutations.addAssignees(issueIds, added);
        p.then(() => {
          toastManager.add({ title: `Assignees updated on ${reached}`, type: "success" });
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.BulkUpdateAssignees,
            data: { view, scope, operation: BulkOperation.Add, mode, count: added.length },
          });
        }).catch((err) => toastMutationError(err, "Failed to update assignees"));
      }

      if (removed.length > 0) {
        const p = selectAllWhere
          ? bulkMutations.removeAssigneesByWhere(selectAllWhere, removed)
          : bulkMutations.removeAssignees(issueIds, removed);
        p.then(() => {
          toastManager.add({ title: `Assignees updated on ${reached}`, type: "success" });
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.BulkUpdateAssignees,
            data: { view, scope, operation: BulkOperation.Remove, mode, count: removed.length },
          });
        }).catch((err) => toastMutationError(err, "Failed to update assignees"));
      }
    },
    [bulkMutations, issueIds, selectAllWhere, view, scope, track, reached],
  );

  const applySetPrimary = useCallback(
    (entityId: string) => {
      const p = selectAllWhere
        ? bulkMutations.setPrimaryAssigneeByWhere(selectAllWhere, entityId)
        : bulkMutations.setPrimaryAssignee(issueIds, entityId);
      p.then(() => {
        toastManager.add({ title: `Primary assignee set on ${reached}`, type: "success" });
        track({
          context: AnalyticsContext.Issue,
          event: IssueEvent.BulkUpdateAssignees,
          data: {
            view,
            scope,
            operation: BulkOperation.SetPrimary,
            mode: selectAllWhere ? BulkMode.Where : BulkMode.Ids,
          },
        });
      }).catch((err) => toastMutationError(err, "Failed to update assignees"));
    },
    [bulkMutations, issueIds, selectAllWhere, view, scope, track, reached],
  );

  const handleValueChange = useCallback(
    (nextIds: string[]) => {
      if (confirmFirst) setPending({ kind: "value", ids: nextIds });
      else applyValueChange(nextIds);
    },
    [confirmFirst, applyValueChange],
  );

  const handleSetPrimary = useCallback(
    (entityId: string) => {
      if (confirmFirst) setPending({ kind: "primary", entityId });
      else applySetPrimary(entityId);
    },
    [confirmFirst, applySetPrimary],
  );

  const selectedRefs = useMemo(() => {
    return selectedRows.flatMap((r) => r.assignees);
  }, [selectedRows]);

  if (pending) {
    return (
      <BulkConfirmPanel
        title={
          pending.kind === "primary"
            ? `Change the primary assignee on ${pluralProjects(count)}?`
            : `Change the assignees on ${pluralProjects(count)}?`
        }
        confirmLabel="Change"
        onConfirm={() => {
          if (pending.kind === "primary") applySetPrimary(pending.entityId);
          else applyValueChange(pending.ids);
          setPending(null);
        }}
        onCancel={() => setPending(null)}
      />
    );
  }

  return (
    <AssigneeSelector
      variant="inline"
      // The builder rung (360): list mode trails each row with "Set
      // primary", which the picker rung (Teams, Stage, Source) cannot seat.
      contentSize="builder"
      mode="list"
      allowSetPrimary
      showSetPrimaryOnAll
      showPrimarySubtext={false}
      value={value}
      onValueChange={handleValueChange}
      onSetPrimary={handleSetPrimary}
      selectedRefs={selectedRefs}
    />
  );
}

// ---------------------------------------------------------------------------
// Unarchive, Teams, Source, Move to Workflow, Custom Field
// ---------------------------------------------------------------------------

/**
 * The glyph Archive hands its slot to when EVERY selected row is archived (a
 * list showing archived rows): the same icon-only seat beside the trash,
 * drawing `archive-restore`. A mixed selection keeps Archive, which skips
 * the archived rows — two glyphs for one slot would read as a choice.
 */
export function buildBulkUnarchiveAction({
  view,
  scope,
  bulkMutations,
  track,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  track: ReturnType<typeof useAnalytics>["track"];
}>): BulkActionDirect<IssueRow> {
  return {
    id: "unarchive",
    type: "direct",
    label: "Unarchive",
    icon: "archive-restore",
    iconOnly: true,
    hidden: (rows, selectAllGroups) =>
      selectAllGroups.length > 0 ||
      rows.length === 0 ||
      rows.some((r) => !r.archived),
    disabledReason: (rows) =>
      rows.some((r) => !r.viewerCanArchive)
        ? "Includes a project you can't unarchive"
        : undefined,
    successMessage: (rows) => `${pluralProjects(rows.length)} unarchived`,
    onAction: async (rows) => {
      await bulkMutations.unarchiveIssues({ issueIds: rows.map((r) => r.issueId) });
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.BulkUnarchive,
        data: { view, scope, mode: BulkMode.Ids, count: rows.length },
      });
    },
  };
}

/** The shared hidden rule for the per-row property writers: no where-based
 *  mutation stands behind them, so a select-all selection (unloaded rows)
 *  is out of reach — the rule bulk Delete and Archive already follow. */
const hiddenUnderSelectAll = (
  _rows: IssueRow[],
  selectAllGroups: SelectAllGroup[],
): boolean => selectAllGroups.length > 0;

/**
 * Teams, the team-level twin of Assignees: a strip action with the same
 * live-editing body — toggling a team adds it to or removes it from every
 * selected row at once, each row keeping the teams the rows did not share.
 */
export function buildBulkTeamsAction({
  view,
  scope,
  bulkMutations,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
}>): BulkActionPopover<IssueRow> {
  return {
    id: "set-teams",
    type: "popover",
    label: "Teams",
    dialogLabel: "Set Teams...",
    icon: PROPERTY_ICON.team,
    contentProps: { padding: "none" },
    hidden: hiddenUnderSelectAll,
    content: ({ selectedRows }) => (
      <BulkTeamsContent
        view={view}
        scope={scope}
        bulkMutations={bulkMutations}
        selectedRows={selectedRows}
      />
    ),
  };
}

export function BulkTeamsContent({
  view,
  scope,
  bulkMutations,
  selectedRows,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  selectedRows: IssueRow[];
}>) {
  const { track } = useAnalytics();
  // The union of every selected row's teams: a checked row means "at least
  // one selected project carries this team", the way Assignees reads.
  const initialIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of selectedRows) for (const t of row.teams) ids.add(t.teamId);
    return Array.from(ids);
  }, [selectedRows]);
  const [value, setValue] = useState(initialIds);
  const prevRef = useRef(initialIds);
  // Toggles go out one after another: the server reads each issue's teams
  // just before it writes, so a toggle sent while the last is still in
  // flight could read the teams from before it.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  // Each toggle sends only what changed. The server applies it to every
  // issue's current teams, so a row keeps the teams the selection did not
  // share, and a second toggle builds on the first.
  const handleValueChange = useCallback(
    (nextIds: string[]) => {
      const before = prevRef.current;
      const prevSet = new Set(before);
      const nextSet = new Set(nextIds);
      const added = nextIds.filter((id) => !prevSet.has(id));
      const removed = before.filter((id) => !nextSet.has(id));
      prevRef.current = nextIds;
      setValue(nextIds);
      if (added.length === 0 && removed.length === 0) return;

      const issueIds = selectedRows.map((r) => r.issueId);
      queueRef.current = queueRef.current
        .then(() => bulkMutations.changeTeams({ issueIds }, added, removed))
        .then(() => {
          toastManager.add({ title: "Teams updated", type: "success" });
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.BulkUpdateTeams,
            data: {
              view,
              scope,
              operation: added.length > 0 ? BulkOperation.Add : BulkOperation.Remove,
              mode: BulkMode.Ids,
              count: selectedRows.length,
            },
          });
        })
        .catch((err: unknown) => {
          // Show the picker as it was before this toggle. Rows the server did
          // change come back with the lists' refetch.
          prevRef.current = before;
          setValue(before);
          toastMutationError(err, "Failed to update teams");
        });
    },
    [bulkMutations, selectedRows, view, scope, track],
  );

  return (
    <TeamSelector
      variant="inline"
      value={value}
      onValueChange={handleValueChange}
    />
  );
}

/**
 * Source, dialog-only: set once at creation and rarely corrected, so it is a
 * correction tool rather than a daily one. The body is the same creatable
 * Source list the property row opens; a pick commits to every row.
 */
export function buildBulkSourceAction({
  view,
  scope,
  bulkMutations,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
}>): BulkActionPopover<IssueRow> {
  return {
    id: "set-source",
    type: "popover",
    label: "Source",
    dialogLabel: "Change Source...",
    icon: PROPERTY_ICON.issueSource,
    overflow: true,
    contentProps: { padding: "none" },
    hidden: hiddenUnderSelectAll,
    content: ({ selectedRows, commit }) => (
      <BulkSourceContent
        view={view}
        scope={scope}
        bulkMutations={bulkMutations}
        selectedRows={selectedRows}
        commit={commit}
      />
    ),
  };
}

export function BulkSourceContent({
  view,
  scope,
  bulkMutations,
  selectedRows,
  commit,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  selectedRows: IssueRow[];
  commit: BulkCommit<IssueRow>;
}>) {
  const { track } = useAnalytics();
  // Checked only when every selected row already carries the same source.
  const shared = useMemo(() => {
    const first = selectedRows[0]?.issueSourceId ?? null;
    return selectedRows.every((r) => (r.issueSourceId ?? null) === first)
      ? first
      : null;
  }, [selectedRows]);
  return (
    <IssueSourceSelector
      variant="inline"
      value={shared}
      onValueChange={(issueSourceId) =>
        commit(
          async (rows) => {
            await bulkMutations.setSource(
              { issueIds: rows.map((r) => r.issueId) },
              issueSourceId,
            );
            track({
              context: AnalyticsContext.Issue,
              event: IssueEvent.BulkUpdateSource,
              data: { view, scope, mode: BulkMode.Ids, count: rows.length },
            });
          },
          `${pluralProjects(selectedRows.length)} updated`,
        )
      }
    />
  );
}

/**
 * Move to Workflow, dialog-only — the bulk form of the row menu's item. Two
 * steps in one body: the target workflow (every OTHER workflow the viewer
 * can edit into), then a stage in it, because a move never lands blind on
 * the target's default. A selection spanning workflows excludes none.
 */
export function buildBulkMoveWorkflowAction({
  view,
  scope,
  bulkMutations,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
}>): BulkActionPopover<IssueRow> {
  return {
    id: "move-workflow",
    type: "popover",
    label: "Move to Workflow",
    dialogLabel: "Move to Workflow...",
    // A move is a verb, not a property: it lists under "Apply an action".
    group: "apply",
    icon: PROPERTY_ICON.workflow,
    overflow: true,
    contentProps: { padding: "none" },
    hidden: hiddenUnderSelectAll,
    content: ({ selectedRows, commit }) => (
      <BulkMoveWorkflowContent
        view={view}
        scope={scope}
        bulkMutations={bulkMutations}
        selectedRows={selectedRows}
        commit={commit}
      />
    ),
  };
}

export function BulkMoveWorkflowContent({
  view,
  scope,
  bulkMutations,
  selectedRows,
  commit,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  selectedRows: IssueRow[];
  commit: BulkCommit<IssueRow>;
}>) {
  const { track } = useAnalytics();
  const { workflowMap } = useWorkspaceData();
  const [targetId, setTargetId] = useState<string | null>(null);
  // A single-workflow selection has nowhere to move within itself.
  const exclude = useMemo(() => {
    const ids = new Set(selectedRows.map((r) => r.workflowId));
    return ids.size === 1 ? Array.from(ids) : undefined;
  }, [selectedRows]);
  const target = targetId ? workflowMap.get(targetId) : undefined;

  if (!targetId) {
    return (
      <WorkflowSelector
        variant="inline"
        editableOnly
        exclude={exclude}
        value={null}
        onValueChange={(ids) => setTargetId(ids[0] ?? null)}
      />
    );
  }
  return (
    <Box>
      <EntitySelectorStepHeader
        title={target?.name ?? "Stage"}
        onBack={() => setTargetId(null)}
      />
      <StageSelector
        variant="inline"
        workflowId={targetId}
        onValueChange={(stageId) =>
          commit(
            async (rows) => {
              await bulkMutations.moveToWorkflow(
                { issueIds: rows.map((r) => r.issueId) },
                targetId,
                stageId,
              );
              // The move's own refetch already reloads every open list and
              // group count; a second pass here would only repeat it.
              track({
                context: AnalyticsContext.Issue,
                event: IssueEvent.BulkMoveWorkflow,
                data: { view, scope, mode: BulkMode.Ids, count: rows.length },
              });
            },
            `${pluralProjects(selectedRows.length)} moved to ${target?.name ?? "workflow"}`,
          )
        }
      />
    </Box>
  );
}

/** Field types a batch cannot sensibly share one value of: uploads and an
 *  address are per project, a formula is derived and nothing writes it. */
const BULK_EXCLUDED_FIELD_TYPES = new Set(["photos", "files", "formula", "address"]);

/**
 * Set Custom Field, dialog-only: the ClickUp "Custom Fields" slot without a
 * strip button per field. Two steps — WHICH field (any workspace field; one
 * a row's type lacks is added to that row), then its editor — and one Apply
 * writes the value onto every row; Clear empties it on every row.
 */
export function buildBulkCustomFieldAction({
  view,
  scope,
  bulkMutations,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
}>): BulkActionPopover<IssueRow> {
  return {
    id: "set-custom-field",
    type: "popover",
    label: "Custom Field",
    dialogLabel: "Set Custom Field...",
    icon: "text-cursor-input",
    overflow: true,
    contentProps: { padding: "none", width: 360 },
    hidden: hiddenUnderSelectAll,
    content: ({ selectedRows, commit }) => (
      <BulkCustomFieldContent
        view={view}
        scope={scope}
        bulkMutations={bulkMutations}
        selectedRows={selectedRows}
        commit={commit}
      />
    ),
  };
}

export function BulkCustomFieldContent({
  view,
  scope,
  bulkMutations,
  selectedRows,
  commit,
}: Omittable<{
  view: AnalyticsView;
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  selectedRows: IssueRow[];
  commit: BulkCommit<IssueRow>;
}>) {
  const { track } = useAnalytics();
  const { customFields } = useWorkspaceData();
  const [fieldId, setFieldId] = useState<string | null>(null);
  // DTO-space draft (through `fieldDto`), the way the migration dialog keeps
  // its answers; the renderer reads the form-space projection of it.
  const [draft, setDraft] = useState<unknown>(null);
  // A text-like editor reports its value on blur, and the blur lands on the
  // Apply press's mousedown. The ref holds that value for the click that
  // follows, before a re-render could; `typed` turns Apply on at the first
  // keystroke, so that press is not swallowed by a disabled button.
  const draftRef = useRef<unknown>(null);
  const [typed, setTyped] = useState(false);
  const resetDraft = () => {
    draftRef.current = null;
    setDraft(null);
    setTyped(false);
  };

  // The fields on offer: every workspace field, minus the types a batch
  // cannot share a value of. A field that is not on a row's type is added to
  // that row, as the Project panel's "Add field from workspace" does: a value
  // record is what puts a field on a Project.
  const exclude = useMemo(
    () =>
      customFields
        .filter((f) => BULK_EXCLUDED_FIELD_TYPES.has(f.type))
        .map((f) => f.customFieldId),
    [customFields],
  );

  const field = useMemo<CustomFieldDef | undefined>(
    () => (fieldId ? customFields.find((f) => f.customFieldId === fieldId) : undefined),
    [customFields, fieldId],
  );

  const apply = useCallback(
    (value: unknown) => {
      if (!field) return;
      commit(
        async (rows) => {
          await bulkMutations.setCustomFieldValue(
            { issueIds: rows.map((r) => r.issueId) },
            field.customFieldId,
            value,
          );
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.BulkUpdateCustomField,
            data: {
              view,
              scope,
              mode: BulkMode.Ids,
              count: rows.length,
              cleared: value === undefined,
            },
          });
        },
        `${pluralProjects(selectedRows.length)} updated`,
      );
    },
    [bulkMutations, commit, field, scope, selectedRows.length, track, view],
  );

  if (!field) {
    return (
      <CustomFieldSelector
        variant="inline"
        maxSelections={1}
        exclude={exclude}
        value={null}
        onValueChange={(ids) => {
          resetDraft();
          setFieldId(ids[0] ?? null);
        }}
      />
    );
  }

  const formValue = draft == null ? null : dataToField(field.type, draft).fieldValue;
  return (
    <Box>
      <EntitySelectorStepHeader title={field.name} onBack={() => setFieldId(null)} />
      <Box padding="sm" gap="sm" onInput={() => setTyped(true)}>
        <CustomFieldRenderer
          field={field}
          value={formValue}
          onChangeValue={(next) => {
            const dto = next == null ? null : fieldDto(field.type, next);
            draftRef.current = dto;
            setDraft(dto);
          }}
          isActive
          autoFocus
          surface="default"
        />
        <Box direction="row" justify="end" gap="xs">
          <Button variant="ghost" size="sm" onClick={() => apply(undefined)}>
            Clear
          </Button>
          <Button
            size="sm"
            disabled={draft == null && !typed}
            onClick={() => {
              if (draftRef.current != null) apply(draftRef.current);
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
